import React, { useEffect, useState } from 'react';
import { Input } from '../common/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../common/Popover';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/utils/utils';
import { ReactDayPickerProps } from './ReactDayPicker';
import { Button } from '../common/Button';
import { AlarmCustomIcon } from '../icon/AlarmCustomIcon';

type CustimeTipmePickerProps = ReactDayPickerProps & {
    value: any,
    onChange: any,
    className?: string,
    popoverContentClassName?: string,
    placeholder?: string,
    disableChangeMinute?: boolean
};

const CustomTimePickerVer2 = ({ value, onChange, className, popoverContentClassName, placeholder, disableChangeMinute = false, ...props }: CustimeTipmePickerProps) => {
    const [hour, setHour] = useState(value ? parseInt(value.split(':')[0]) : '');
    const [minute, setMinute] = useState(value ? parseInt(value.split(':')[1]) : '');
    const [isOpen, setIsOpen] = useState(false);

    const formatTime = num => String(num).padStart(2, '0');

    useEffect(() => {
        if (value) {
            const [h, m] = value.split(':').map(Number);
            setHour(h);
            setMinute(m);
        }
    }, [value]);

    const handleHourChange = (amount) => {
        let curMinute = minute || 0;
        let currentHour = hour || 0;
        setHour((prev) => {
            const newHour = (currentHour + amount + 24) % 24;

            onChange(`${formatTime(newHour)}:${formatTime(curMinute)}`);

            return newHour;
        });
        if (minute === '') {
            setMinute(0);
        }
    };

    const handleMinuteChange = (amount) => {
        let curHour = hour || 0;
        let curMinute = minute || 0;
        setMinute((prev) => {
            const newMinute = (curMinute + amount + 60) % 60;
            onChange(`${formatTime(curHour)}:${formatTime(newMinute)}`);
            return newMinute;
        });
        if (hour === '') {
            setHour(0);
        }
    };

    const handleHourInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newHour = value === '' ? 0 : parseInt(value);
        if (!isNaN(newHour)) {
            const cappedHour = Math.min(Math.max(newHour, 0), 23);
            setHour(cappedHour);
            onChange(`${formatTime(cappedHour)}:${formatTime(minute)}`);
        }
    };

    const handleMinuteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newMinute = value === '' ? 0 : parseInt(value);
        if (!isNaN(newMinute)) {
            const cappedMinute = Math.min(Math.max(newMinute, 0), 59);
            setMinute(cappedMinute);
            onChange(`${formatTime(hour)}:${formatTime(cappedMinute)}`);
        }
    };

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger className='w-full' asChild>
                <div className='relative w-full '>
                    <div className='absolute top-[11px] left-5'><AlarmCustomIcon /></div>
                    <Input
                        readOnly
                        value={(hour !== '' && minute !== '') ? `${formatTime(hour)}:${formatTime(minute)}` : ''}
                        onClick={() => setIsOpen(true)}
                        className={cn(
                            'text-sm font-medium text-left text-text h-10 w-full px-10 cursor-pointer border-border-input-2 bg-white placeholder:text-text',
                            className
                        )}
                        placeholder={placeholder}
                    />
                    <ChevronDown
                        className={cn(
                            'absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 cursor-pointer transition-opacity',
                            isOpen && 'hidden'
                        )}
                        size={14} // Tăng kích thước để dễ nhìn hơn
                        onClick={() => setIsOpen(true)}
                    />
                </div>
            </PopoverTrigger>

            <PopoverContent
                align='start'
                className={cn(
                    'w-full p-2 bg-white shadow-md rounded-lg flex justify-around items-center gap-4 z-[1100]',
                    popoverContentClassName
                )}
            >
                <div className='flex flex-col items-center'>
                    <Button onClick={() => handleHourChange(1)} className='text-red-500 hover:text-primary' size='icon' variant='ghost'>
                        <ChevronUp />
                    </Button>
                    <Input
                        type='number'
                        readOnly
                        value={formatTime(hour)}
                        onChange={handleHourInputChange}
                        className={cn(
                            'text-xl text-center w-12 h-10 p-0 border-none focus:ring-0 bg-light cursor-default',
                            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' // Tắt mũi tên
                        )}
                        min={0}
                        max={23}
                    />
                    <Button onClick={() => handleHourChange(-1)} className='text-red-500 hover:text-primary' size='icon' variant='ghost'>
                        <ChevronDown />
                    </Button>
                </div>

                <span className='text-xl'>:</span>

                <div className='flex flex-col items-center'>
                    {!disableChangeMinute && (
                        <Button
                            onClick={() => handleMinuteChange(30)}
                            className='text-red-500 hover:text-primary'
                            size='icon'
                            variant='ghost'
                        >
                            <ChevronUp />
                        </Button>
                    )}
                    <Input
                        type='number'
                        readOnly
                        value={formatTime(minute)}
                        onChange={disableChangeMinute ? undefined : handleMinuteInputChange}
                        className={cn(
                            'text-xl text-center w-12 h-10 p-0 border-none focus:ring-0 bg-light cursor-default',
                            '[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none' // Tắt mũi tên
                        )}
                        min={0}
                        max={59}
                    />
                    {!disableChangeMinute && (
                        <Button
                            onClick={() => handleMinuteChange(-30)}
                            className='text-red-500 hover:text-primary'
                            size='icon'
                            variant='ghost'
                        >
                            <ChevronDown />
                        </Button>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default CustomTimePickerVer2;
